import { CreditScoreSummary, CriminalRecordsType, EvictionsType } from '../interfaces'

export const REPORT_STATUS = {
  PENDING: 'pending',
  SUCCESS: 'success',
  ERROR: 'error',
  UNAVAILABLE: 'unavailable'
} as const

export type ReportStatus = typeof REPORT_STATUS[keyof typeof REPORT_STATUS]

/**
 * Check if a credit report is pending
 */
export const isCreditReportPending = (creditScoreSummary?: CreditScoreSummary): boolean => {
  return creditScoreSummary?.reportStatus === REPORT_STATUS.PENDING
}

/**
 * Check if a criminal report is pending
 */
export const isCriminalReportPending = (criminalRecords?: CriminalRecordsType): boolean => {
  return criminalRecords?.reportStatus === REPORT_STATUS.PENDING
}

/**
 * Check if an eviction report is pending
 */
export const isEvictionReportPending = (evictions?: EvictionsType): boolean => {
  return evictions?.reportStatus === REPORT_STATUS.PENDING
}

/**
 * Get the display text for a pending report
 */
export const getPendingReportMessage = (reportType: 'credit' | 'criminal' | 'eviction'): string => {
  switch (reportType) {
    case 'credit':
      return 'Credit report is being processed'
    case 'criminal':
      return 'Criminal background check is being processed'
    case 'eviction':
      return 'Eviction records check is being processed'
    default:
      return 'Report is being processed'
  }
}

/**
 * Check if any reports are pending
 */
export const hasAnyPendingReports = (
  creditScoreSummary?: CreditScoreSummary,
  criminalRecords?: CriminalRecordsType,
  evictions?: EvictionsType
): boolean => {
  return (
    isCreditReportPending(creditScoreSummary) ||
    isCriminalReportPending(criminalRecords) ||
    isEvictionReportPending(evictions)
  )
}

/**
 * Get all pending report types
 */
export const getPendingReportTypes = (
  creditScoreSummary?: CreditScoreSummary,
  criminalRecords?: CriminalRecordsType,
  evictions?: EvictionsType
): string[] => {
  const pendingTypes: string[] = []
  
  if (isCreditReportPending(creditScoreSummary)) {
    pendingTypes.push('credit')
  }
  if (isCriminalReportPending(criminalRecords)) {
    pendingTypes.push('criminal')
  }
  if (isEvictionReportPending(evictions)) {
    pendingTypes.push('eviction')
  }
  
  return pendingTypes
}
